import { useTranslation } from 'react-i18next'
import {
  CheckboxProps,
  ComboboxGroupItem,
  ComboboxGroupProps,
  ComboboxItem,
  ComboboxProps,
  InputProps,
  RadioOption,
  RadioProps,
  SingleDateProps,
} from '@node-space/ui-components'
import { IDateValue } from 'types/types'
import { useDateInput } from './useDateInput'

type BaseFieldProps<T> = {
  key: T
  placeholder?: string
  showLabel?: boolean
}

type InputFieldProps<T> = BaseFieldProps<T> & {
  useLabelAsPlaceholder?: boolean
  type?: 'text' | 'email' | 'password'
}

type RadioFieldProps<T> = {
  key: T
  options: RadioOption[]
  onChangeAdditional?: (value: string) => void
}

type ComboboxFieldProps<T> = BaseFieldProps<T> & {
  key: T
  items: ComboboxItem[] | ComboboxGroupItem[]
  onChangeAdditional?: (value: string) => void
  defaultValue?: string
}

type CheckBoxFieldProps<T> = {
  key: T
  onChangeAdditional?: (value: boolean | number) => void
}

type CheckboxFieldProps<T> = {
  key: T
  onChangeAdditional?: (value: boolean | number) => void
  testId: string
  id: string
  name: string
  label?: string
}

type DatepickerFieldProps<T> = {
  key: T
}

/**
 * This hook contains functions for setting form field props
 * using react-hook-form foundation.
 * @param formSchema The form schema set up with yup.
 * @param form The options exposed by react-hook-form useForm hook.
 * @param formValues The form values as controlled by react-hook-form useWatch hook.
 * @template T The form keys Type used for key values.
 * @returns functions for setting various types of form field props.
 */
export const useFormFields = <T>(formSchema, form, formValues) => {
  const { t } = useTranslation()
  const dateOfBirth = useDateInput()

  const {
    register,
    setValue,
    formState: { errors },
  } = form

  /**
   * This function gets the field label set in form schema.
   * @param {T} key The field key.
   * @returns string
   */
  const getLabel = (key: T): string => formSchema?.fields?.[key]?.spec?.label

  /**
   * This function is used for setting shared field props.
   * @param {T} key The field key.
   * @param {String} placeholder Optional value for placeholder.
   * No placeholder if omitted.
   * @param {Boolean} showLabel Optionally shows label. False if omitted.
   * @returns form field props
   */
  const setBaseField = ({ key, placeholder, showLabel = true }: BaseFieldProps<T>) => ({
    ...register(key),
    ...(placeholder && { placeholder }),
    ...(showLabel && { label: getLabel(key) }),
    value: formValues[key],
    error: !!errors[key],
    errorText: errors[key]?.message,
  })

  /**
   * This function is used for setting an input field props.
   * @param {T} key The field key.
   * @param {String} placeholder Optional value for placeholder.
   * @param {Boolean} useLabelAsPlaceholder Optionally uses label text as input placeholder.
   * False if omitted.
   * @param {Boolean} showLabel Optionally shows label. True if omitted.
   * @param {String} type Optional value to change input type.
   * - Options are "text" | "email" | "password".
   * - "text" if omitted.
   * @returns form field props
   */
  const setInputField = ({
    key,
    placeholder,
    useLabelAsPlaceholder,
    showLabel,
    type,
  }: InputFieldProps<T>): InputProps => ({
    ...setBaseField({
      key,
      placeholder: useLabelAsPlaceholder ? getLabel(key) : placeholder,
      showLabel,
    }),
    type: type ?? 'text',
  })

  /**
   * This function is used for setting a number input field props.
   * @param {T} key The field key.
   * @param {String} placeholder Optional value for placeholder.
   * @param {Boolean} useLabelAsPlaceholder Optionally uses label text as input placeholder.
   * False if omitted.
   * @param {Boolean} showLabel Optionally shows label. True if omitted.
   * @returns form field props
   */
  const setNumberInputField = ({
    key,
    placeholder,
    useLabelAsPlaceholder,
    showLabel,
  }: InputFieldProps<T>): InputProps => ({
    ...setBaseField({
      key,
      placeholder: useLabelAsPlaceholder ? getLabel(key) : placeholder,
      showLabel,
    }),
    inputMode: 'numeric',
    onChange: ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
      const isNotValidNumber = Number.isNaN(Number(value))
      if (value && isNotValidNumber) return
      setValue(key, value, { shouldValidate: true, shouldDirty: true })
    },
  })

  const setComboboxField = ({
    key,
    placeholder,
    showLabel,
    items,
    defaultValue,
    onChangeAdditional,
  }: ComboboxFieldProps<T>): ComboboxProps | ComboboxGroupProps => ({
    ...setBaseField({ key, placeholder, showLabel }),
    items,
    onChange: value => {
      !!onChangeAdditional && onChangeAdditional(value)
      setValue(key, value, { shouldValidate: true, shouldDirty: true })
    },
    defaultValue,
  })

  /**
   * This function is used for setting a radio field props.
   * @param {T} key The field key.
   * @param {RadioOption[]} options The radio options array.
   * @param {Function} onChangeAdditional An optional function for any additional logic when
   * the value is changed, like resetting errors. Recieves the updated value and returns void.
   * @returns form field props
   */
  const setRadioField = ({ key, options, onChangeAdditional }: RadioFieldProps<T>): RadioProps => ({
    ...setBaseField({ key }),
    options,
    onChange: value => {
      !!onChangeAdditional && onChangeAdditional(value)
      setValue(key, value, { shouldValidate: true, shouldDirty: true })
    },
  })

  /**
   * This function is used for setting an email field props.
   * @param {T} key The field key.
   * @returns form field props
   */
  const setEmailField = ({ key }: InputFieldProps<T>): InputProps => ({
    ...setBaseField({
      key,
      placeholder: t('yourEmailAddress'),
    }),
    type: 'email',
    inputMode: 'email',
  })

  const setCheckboxField = ({
    key,
    onChangeAdditional,
    testId,
    id,
    name,
  }: CheckboxFieldProps<T>): CheckboxProps => ({
    label: getLabel(key),
    onCheckedChange: () => {
      const value = !formValues[key]
      !!onChangeAdditional && onChangeAdditional(value)
      setValue(key, value, { shouldValidate: true, shouldDirty: true })
    },
    checked: formValues[key],
    testId,
    id,
    name,
  })

  /**
   * This function is used for setting a single date field props.
   * @param {T} key The field key.
   * @returns form field props
   */
  const setSingleDateField = (key: T): SingleDateProps => ({
    ...register(key),
    labels: {
      ...dateOfBirth?.singleDateLabels,
      errorMessage: dateOfBirth?.getDateErrorText(errors?.[key]),
    },
    date: dateOfBirth?.getDateFields(formValues?.[key]),
    errors: dateOfBirth?.getDateErrors(errors?.[key]),
    onChange: value =>
      setValue(key, dateOfBirth?.setDateInputValue(value), {
        shouldValidate: true,
        shouldDirty: true,
      }),
    onValidate: dateOfBirthErrors => dateOfBirth?.setDateErrors(dateOfBirthErrors),
  })

  /**
   * This function is used for setting a datepicker field props.
   * @param {T} key The field key.
   * @returns form field props
   */
  const setDatePickerField = ({ key }: DatepickerFieldProps<T>) => ({
    dateFieldLabel: getLabel(key),
    onDateValueChange: (dateVal: IDateValue) =>
      setValue(key, dateVal?.startDate, { shouldValidate: true, shouldDirty: true }),
    dateValue: formValues[key],
  })

  return {
    getLabel,
    setBaseField,
    setInputField,
    setNumberInputField,
    setComboboxField,
    setRadioField,
    setEmailField,
    setSingleDateField,
    setCheckboxField,
    setDatePickerField,
  }
}
